import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import DateTimePicker from "../utils/DateTimePicker";

import {
  getOtherMedicalAvailability,
  saveOtherMedicalAvailability,
  deleteOtherMedicalAvailability,
  checkOtherMedicalAvailability,
} from "../../redux/slices/availabilitySlice.js/otherMedicalAvailabilitySlice";

import UserInfo from "./AllAvailabilities";

// ============================================================
// DAYS
// ============================================================

const days = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

// ============================================================
// DEFAULT HALVES
// ============================================================

const defaultHalves = [
  {
    name: "First Half",
    startTime: "09:00",
    endTime: "13:00",
    isOpen: false,
  },
  {
    name: "Second Half",
    startTime: "14:00",
    endTime: "18:00",
    isOpen: false,
  },
];

// ============================================================
// DATE HELPERS
// ============================================================

const formatLocalDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const getTodayDate = () => {
  const today = new Date();

  today.setHours(0, 0, 0, 0);

  return formatLocalDate(today);
};

// ============================================================
// NEXT 7 DAYS
// ============================================================

const getNext7Days = () => {
  const today = new Date();

  today.setHours(0, 0, 0, 0);

  return Array.from({ length: 7 }, (_, index) => {
    const date = new Date(today);

    date.setDate(today.getDate() + index);

    return {
      day: days[date.getDay()],
      date: formatLocalDate(date),
      fullDate: date,
    };
  });
};

// ============================================================
// CREATE DEFAULT WEEK
// ============================================================

const createDefaultWeek = () =>
  getNext7Days().map((item) => ({
    day: item.day,
    date: item.date,
    isHoliday: false,

    halves: defaultHalves.map((half) => ({
      ...half,
    })),
  }));

// ============================================================
// NORMALIZE BACKEND DATA
// ============================================================

const normalizeAvailability = (source) => {
  if (!source) {
    return null;
  }

  let availability = source;

  if (!Array.isArray(availability)) {
    if (Array.isArray(source.availability)) {
      availability = source.availability;
    } else if (Array.isArray(source.data)) {
      availability = source.data;
    } else {
      availability = [source];
    }
  }

  if (!availability.length) {
    return null;
  }

  return availability.map((day) => ({
    day: day.day || "",
    date: day.date || "",
    isHoliday: Boolean(day.isHoliday),

    halves: defaultHalves.map((defaultHalf) => {
      const existingSlot = Array.isArray(day.openingTimings)
        ? day.openingTimings.find((slot) => slot.name === defaultHalf.name)
        : null;

      if (!existingSlot) {
        return {
          ...defaultHalf,
          isOpen: false,
        };
      }

      return {
        ...defaultHalf,
        ...existingSlot,
        isOpen: true,
      };
    }),
  }));
};

// ============================================================
// COMPONENT
// ============================================================

const OtherMedicalAvailability = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // ==========================================================
  // REDUX
  // ==========================================================

  const {
    list: data,
    single,
    loading,
    error,
  } = useSelector((state) => state.otherMedicalAvailability || {});

  const reduxToken = useSelector((state) => state.auth?.token);

  const reduxUser = useSelector((state) => state.auth?.user);

  // ==========================================================
  // AUTH
  // ==========================================================

  const token = reduxToken || localStorage.getItem("token");

  let storedUser = null;

  try {
    storedUser = JSON.parse(localStorage.getItem("user"));
  } catch (err) {
    console.error("Invalid user data in localStorage:", err);
  }

  const user = reduxUser || storedUser;

  // ==========================================================
  // LOCAL STATE
  // ==========================================================

  const [weekly, setWeekly] = useState(createDefaultWeek());

  // ==========================================================
  // FETCH AVAILABILITY
  // ==========================================================

  useEffect(() => {
    if (!token) {
      return;
    }

    dispatch(
      getOtherMedicalAvailability({
        token,
      }),
    );
  }, [token, dispatch]);

  // ==========================================================
  // LOAD BACKEND DATA
  // ==========================================================

  useEffect(() => {
    const source = single ?? data;

    const normalized = normalizeAvailability(source);

    if (normalized) {
      setWeekly(normalized);
    }
  }, [single, data]);

  // ==========================================================
  // LOGIN CHECK
  // ==========================================================

  if (!token || !user) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50 p-6">
        <div className="w-full max-w-md rounded-3xl border border-red-200 bg-white p-7 text-center shadow-[0_15px_45px_rgba(15,31,51,0.10)]">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-red-50 text-2xl">
            🔐
          </div>

          <h2 className="text-lg font-bold text-slate-800">Login Required</h2>

          <p className="mt-2 text-sm text-slate-500">
            Please login first to manage your medical availability.
          </p>

          <button
            type="button"
            onClick={() => navigate(-1)}
            className="
              mt-5
              rounded-xl
              px-5
              py-2.5
              text-sm
              font-bold
              text-white
              shadow-md
              transition-all
              hover:-translate-y-0.5
            "
            style={{
              backgroundImage:
                "linear-gradient(135deg, rgb(26, 58, 92), rgb(14, 165, 233))",
            }}
          >
            ← Go Back
          </button>
        </div>
      </div>
    );
  }

  // ==========================================================
  // DATE HELPERS
  // ==========================================================

  const isToday = (date) => {
    if (!date) {
      return false;
    }

    return date === getTodayDate();
  };

  // ==========================================================
  // TOGGLE HOLIDAY
  // ==========================================================

  const toggleHoliday = (dayIndex) => {
    setWeekly((previous) =>
      previous.map((day, index) => {
        if (index !== dayIndex) {
          return day;
        }

        const newHolidayStatus = !day.isHoliday;

        return {
          ...day,

          isHoliday: newHolidayStatus,

          halves: newHolidayStatus
            ? day.halves.map((half) => ({
                ...half,
                isOpen: false,
              }))
            : day.halves.map((half) => ({
                ...half,
              })),
        };
      }),
    );
  };

  // ==========================================================
  // TOGGLE HALF
  // ==========================================================

  const toggleHalf = (dayIndex, halfIndex, value) => {
    setWeekly((previous) =>
      previous.map((day, index) => {
        if (index !== dayIndex) {
          return day;
        }

        return {
          ...day,

          halves: day.halves.map((half, currentHalfIndex) =>
            currentHalfIndex === halfIndex
              ? {
                  ...half,
                  isOpen: value,
                }
              : half,
          ),
        };
      }),
    );
  };

  // ==========================================================
  // UPDATE HALF TIME
  // ==========================================================

  const updateHalfTime = (dayIndex, halfIndex, field, value) => {
    const currentDay = weekly[dayIndex];

    if (!currentDay) {
      return;
    }

    const currentHalf = currentDay.halves[halfIndex];

    if (!currentHalf) {
      return;
    }

    const updatedHalf = {
      ...currentHalf,
      [field]: value,
    };

    if (
      updatedHalf.startTime &&
      updatedHalf.endTime &&
      updatedHalf.startTime >= updatedHalf.endTime
    ) {
      alert("Start time must be before end time");
      return;
    }

    setWeekly((previous) =>
      previous.map((day, currentDayIndex) => {
        if (currentDayIndex !== dayIndex) {
          return day;
        }

        return {
          ...day,

          halves: day.halves.map((half, currentHalfIndex) =>
            currentHalfIndex === halfIndex ? updatedHalf : half,
          ),
        };
      }),
    );
  };

  // ==========================================================
  // UPDATE DATE
  // ==========================================================

  const updateWeeklyDate = (dayIndex, newDate) => {
    if (!newDate) {
      return;
    }

    const duplicateDate = weekly.some(
      (day, index) => index !== dayIndex && day.date === newDate,
    );

    if (duplicateDate) {
      alert("❌ Date already exists!");
      return;
    }

    setWeekly((previous) =>
      previous.map((day, index) => {
        if (index !== dayIndex) {
          return day;
        }

        const selectedDate = new Date(`${newDate}T00:00:00`);

        return {
          ...day,

          date: newDate,

          day: days[selectedDate.getDay()],
        };
      }),
    );
  };

  // ==========================================================
  // COPY DAY AVAILABILITY
  // ==========================================================

  const copyDayAvailability = (dayIndex) => {
    const template = weekly[dayIndex];

    if (!template) {
      return;
    }

    setWeekly((previous) =>
      previous.map((day, index) => {
        if (index === dayIndex) {
          return day;
        }

        return {
          ...day,

          isHoliday: template.isHoliday,

          halves: template.halves.map((half) => ({
            ...half,
          })),
        };
      }),
    );
  };

  // ==========================================================
  // DELETE DAY
  // ==========================================================

  const handleDeleteDay = async (date) => {
    if (!date) {
      alert("❌ Invalid date.");
      return;
    }

    const confirmed = window.confirm(`Delete availability for ${date}?`);

    if (!confirmed) {
      return;
    }

    try {
      const result = await dispatch(
        deleteOtherMedicalAvailability({
          date,
          token,
        }),
      );

      if (deleteOtherMedicalAvailability.fulfilled.match(result)) {
        alert("✅ Availability deleted successfully.");

        await dispatch(
          getOtherMedicalAvailability({
            token,
          }),
        );

        return;
      }

      alert(result?.payload?.message || "❌ Failed to delete availability.");
    } catch (err) {
      console.error("Delete availability failed:", err);

      alert("❌ Unable to delete availability.");
    }
  };

  // ==========================================================
  // BUILD SAVE PAYLOAD
  // ==========================================================

  const buildPayload = () => ({
    availability: weekly.map((day) => ({
      day: day.day,

      date: day.date,

      isHoliday: Boolean(day.isHoliday),

      openingTimings: day.halves
        .filter((half) => half.isOpen)
        .map((half) => ({
          name: half.name,
          startTime: half.startTime,
          endTime: half.endTime,
        })),
    })),

    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  });

  // ==========================================================
  // VALIDATE LOCAL DATA
  // ==========================================================

  const validateWeeklyData = () => {
    if (!weekly.length) {
      alert("❌ No availability data found.");
      return false;
    }

    const dates = new Set();

    for (const day of weekly) {
      if (!day.date) {
        alert("❌ Every availability day must have a date.");

        return false;
      }

      if (dates.has(day.date)) {
        alert(`❌ Duplicate date found: ${day.date}`);

        return false;
      }

      dates.add(day.date);

      if (day.isHoliday) {
        continue;
      }

      for (const half of day.halves) {
        if (!half.isOpen) {
          continue;
        }

        if (!half.startTime || !half.endTime) {
          alert(
            `❌ Please provide valid timing for ${half.name} on ${day.day}.`,
          );

          return false;
        }

        if (half.startTime >= half.endTime) {
          alert(
            `❌ Start time must be before end time for ${half.name} on ${day.day}.`,
          );

          return false;
        }
      }
    }

    return true;
  };

  // ==========================================================
  // SAVE
  // ==========================================================

  const handleSave = async () => {
    if (!token) {
      alert("❌ Authentication token missing.");
      return;
    }

    if (!validateWeeklyData()) {
      return;
    }

    const payload = buildPayload();

    try {
      // ------------------------------------------------------
      // CHECK AVAILABILITY
      // ------------------------------------------------------

      const checkResult = await dispatch(
        checkOtherMedicalAvailability({
          data: payload,
          token,
        }),
      );

      if (checkOtherMedicalAvailability.rejected.match(checkResult)) {
        alert(
          checkResult?.payload?.message || "❌ Availability conflict detected.",
        );

        return;
      }

      // ------------------------------------------------------
      // SAVE AVAILABILITY
      // ------------------------------------------------------

      const saveResult = await dispatch(
        saveOtherMedicalAvailability({
          data: payload,
          token,
        }),
      );

      if (saveOtherMedicalAvailability.fulfilled.match(saveResult)) {
        alert("✅ Availability saved successfully.");

        navigate("/dashboard/medical-service");

        return;
      }

      alert(saveResult?.payload?.message || "❌ Failed to save availability.");
    } catch (err) {
      console.error("Save availability failed:", err);

      alert(err?.message || "❌ Unable to save availability.");
    }
  };

  // ==========================================================
  // UI
  // ==========================================================

  return (
    <div className="min-h-screen bg-slate-50">
      {/* ======================================================
          BACKGROUND DECORATION
      ====================================================== */}

      <div className="relative overflow-hidden">
        <div
          className="
            pointer-events-none
            absolute
            -left-40
            -top-40
            h-96
            w-96
            rounded-full
            bg-sky-400/[0.06]
            blur-3xl
          "
        />

        <div
          className="
            pointer-events-none
            absolute
            -right-40
            top-24
            h-[28rem]
            w-[28rem]
            rounded-full
            bg-blue-500/[0.06]
            blur-3xl
          "
        />

        <div className="relative mx-auto max-w-7xl px-4 py-5 sm:px-6 sm:py-7 lg:px-8">
          {/* ==================================================
              BACK BUTTON
          ================================================== */}

          <div className="mb-5">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="
                group
                inline-flex
                items-center
                gap-2
                rounded-xl
                border
                border-slate-200
                bg-white
                px-4
                py-2.5
                text-sm
                font-bold
                text-slate-700
                shadow-sm
                transition-all
                duration-200
                hover:-translate-x-0.5
                hover:border-sky-200
                hover:text-sky-600
                hover:shadow-md
              "
            >
              <span className="text-lg transition-transform group-hover:-translate-x-0.5">
                ←
              </span>
              Back
            </button>
          </div>

          {/* ==================================================
              HEADER
          ================================================== */}

          <div
            className="
              mb-6
              overflow-hidden
              rounded-[2rem]
              border
              border-white/10
              p-6
              shadow-[0_20px_55px_rgba(15,31,51,0.20)]
              sm:p-8
            "
            style={{
              backgroundImage:
                "linear-gradient(145deg, rgb(15, 31, 51) 0%, rgb(26, 58, 92) 50%, rgb(30, 73, 118) 100%)",
            }}
          >
            <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
              <div>
                {/* BADGE */}

                <div
                  className="
                    mb-4
                    inline-flex
                    items-center
                    gap-2
                    rounded-full
                    border
                    border-sky-300/20
                    bg-sky-400/10
                    px-3
                    py-1.5
                    text-[10px]
                    font-bold
                    uppercase
                    tracking-[0.18em]
                    text-sky-200
                    sm:text-xs
                  "
                >
                  <span className="h-2 w-2 rounded-full bg-sky-400 shadow-[0_0_12px_rgba(14,165,233,0.9)]" />
                  Healthcare Scheduling
                </div>

                <h1 className="text-2xl font-extrabold tracking-tight text-white sm:text-4xl">
                  Split Day Availability
                </h1>

                <p className="mt-3 max-w-2xl text-sm leading-6 text-slate-300 sm:text-base">
                  Manage your working hours, holidays, and split-day medical
                  appointment availability.
                </p>
              </div>

              {/* SCHEDULE CARD */}

              <div
                className="
                  flex
                  shrink-0
                  items-center
                  gap-3
                  rounded-2xl
                  border
                  border-white/10
                  bg-white/[0.07]
                  px-5
                  py-4
                  shadow-inner
                  backdrop-blur-md
                "
              >
                <div
                  className="
                    flex
                    h-11
                    w-11
                    items-center
                    justify-center
                    rounded-xl
                    bg-sky-400/15
                    text-xl
                  "
                >
                  📅
                </div>

                <div>
                  <p className="text-xs font-medium text-slate-400">Schedule</p>

                  <p className="mt-0.5 text-sm font-bold text-white">
                    Next 7 Days
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* ==================================================
              USER INFO
          ================================================== */}

          <div
            className="
              mb-6
              overflow-hidden
              rounded-2xl
              border
              border-slate-200
              bg-white
              shadow-[0_8px_30px_rgba(15,31,51,0.07)]
            "
          >
            <UserInfo />
          </div>

          {/* ==================================================
              ERROR
          ================================================== */}

          {error && (
            <div
              className="
                mb-6
                flex
                items-start
                gap-3
                rounded-2xl
                border
                border-red-200
                bg-red-50
                p-4
                text-sm
                font-semibold
                text-red-600
                shadow-sm
              "
            >
              <span className="text-base">⚠️</span>

              <span>
                {typeof error === "string"
                  ? error
                  : error?.message || "Something went wrong."}
              </span>
            </div>
          )}

          {/* ==================================================
              WEEKLY HEADER / LEGEND
          ================================================== */}

          <div
            className="
              mb-6
              rounded-2xl
              border
              border-slate-200
              bg-white
              px-5
              py-4
              shadow-[0_6px_25px_rgba(15,31,51,0.06)]
              sm:px-6
            "
          >
            <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <div className="flex items-center gap-2">
                  <div
                    className="h-2.5 w-2.5 rounded-full"
                    style={{
                      background: "rgb(14, 165, 233)",
                    }}
                  />

                  <h2 className="text-base font-extrabold text-slate-800">
                    Weekly Availability
                  </h2>
                </div>

                <p className="mt-1 text-xs text-slate-500">
                  Configure each day's working slots.
                </p>
              </div>

              <div className="flex flex-wrap items-center gap-4 text-xs font-semibold text-slate-600">
                <div className="flex items-center gap-2">
                  <span
                    className="h-3 w-3 rounded-full shadow-sm"
                    style={{
                      background: "rgb(14, 165, 233)",
                    }}
                  />
                  Working
                </div>

                <div className="flex items-center gap-2">
                  <span className="h-3 w-3 rounded-full bg-red-500" />
                  Holiday
                </div>

                <div className="flex items-center gap-2">
                  <span className="h-3 w-3 rounded-full border border-slate-300 bg-slate-100" />
                  Closed
                </div>
              </div>
            </div>
          </div>

          {/* ==================================================
              WEEKLY AVAILABILITY
          ================================================== */}

          <div className="space-y-6">
            {weekly.map((day, dayIndex) => (
              <div
                key={`${day.date}-${dayIndex}`}
                className="
                  overflow-hidden
                  rounded-[1.75rem]
                  border
                  border-slate-200
                  bg-white
                  shadow-[0_10px_35px_rgba(15,31,51,0.08)]
                  transition-all
                  duration-300
                  hover:shadow-[0_15px_45px_rgba(15,31,51,0.12)]
                "
              >
                {/* ==========================================
                    DAY HEADER
                ========================================== */}

                <div
                  className={`
                    border-b
                    px-4
                    py-5
                    sm:px-6
                    ${
                      day.isHoliday
                        ? "border-red-100 bg-gradient-to-r from-red-50 via-white to-white"
                        : "border-slate-100 bg-gradient-to-r from-slate-50 via-white to-white"
                    }
                  `}
                >
                  <div className="flex flex-col gap-5 xl:flex-row xl:items-center xl:justify-between">
                    {/* DAY INFO */}

                    <div className="flex items-center gap-4">
                      <div
                        className={`
                          flex
                          h-14
                          w-14
                          shrink-0
                          items-center
                          justify-center
                          rounded-2xl
                          text-xl
                          shadow-sm
                          ${
                            day.isHoliday
                              ? "bg-red-100 text-red-600"
                              : "bg-sky-50 text-sky-600"
                          }
                        `}
                      >
                        {day.isHoliday ? "🏖️" : "🩺"}
                      </div>

                      <div>
                        <div className="flex flex-wrap items-center gap-2">
                          <h3 className="text-lg font-extrabold text-slate-800 sm:text-xl">
                            {day.day}
                          </h3>

                          {isToday(day.date) && (
                            <span
                              className="
                                rounded-full
                                px-2.5
                                py-1
                                text-[10px]
                                font-extrabold
                                uppercase
                                tracking-wider
                                text-white
                                shadow-sm
                              "
                              style={{
                                background: "rgb(14, 165, 233)",
                              }}
                            >
                              Today
                            </span>
                          )}
                        </div>

                        <p
                          className={`
                            mt-1
                            text-sm
                            ${
                              isToday(day.date)
                                ? "font-bold text-sky-600"
                                : "text-slate-500"
                            }
                          `}
                        >
                          {day.date}
                        </p>

                        {/* DATE */}

                        <input
                          type="date"
                          value={day.date}
                          min={getTodayDate()}
                          onChange={(event) =>
                            updateWeeklyDate(dayIndex, event.target.value)
                          }
                          className="
                            mt-3
                            rounded-xl
                            border
                            border-slate-200
                            bg-white
                            px-3
                            py-2
                            text-sm
                            font-semibold
                            text-slate-700
                            outline-none
                            transition-all
                            focus:border-sky-400
                            focus:ring-4
                            focus:ring-sky-100
                          "
                        />

                        {day.isHoliday && (
                          <p className="mt-2 text-xs font-extrabold uppercase tracking-wide text-red-600">
                            🚫 Holiday
                          </p>
                        )}
                      </div>
                    </div>

                    {/* ACTION BUTTONS */}

                    <div className="flex flex-wrap gap-2">
                      {/* HOLIDAY */}

                      <button
                        type="button"
                        onClick={() => toggleHoliday(dayIndex)}
                        className={`
                          inline-flex
                          items-center
                          gap-2
                          rounded-xl
                          border
                          px-3
                          py-2.5
                          text-xs
                          font-bold
                          transition-all
                          ${
                            day.isHoliday
                              ? "border-red-500 bg-red-500 text-white shadow-md hover:bg-red-600"
                              : "border-slate-200 bg-white text-slate-600 hover:border-red-300 hover:bg-red-50 hover:text-red-600"
                          }
                        `}
                      >
                        {day.isHoliday ? "✓ Remove Holiday" : "🏖️ Mark Holiday"}
                      </button>

                      {/* COPY */}

                      <button
                        type="button"
                        onClick={() => copyDayAvailability(dayIndex)}
                        className="
                          inline-flex
                          items-center
                          gap-2
                          rounded-xl
                          border
                          border-sky-200
                          bg-sky-50
                          px-3
                          py-2.5
                          text-xs
                          font-bold
                          text-sky-700
                          transition-all
                          hover:bg-sky-100
                          hover:shadow-sm
                        "
                      >
                        📋 Copy
                      </button>

                      {/* DELETE */}

                      <button
                        type="button"
                        onClick={() => handleDeleteDay(day.date)}
                        className="
                          inline-flex
                          items-center
                          gap-2
                          rounded-xl
                          border
                          border-red-200
                          bg-red-50
                          px-3
                          py-2.5
                          text-xs
                          font-bold
                          text-red-600
                          transition-all
                          hover:bg-red-100
                          hover:shadow-sm
                        "
                      >
                        🗑 Delete
                      </button>
                    </div>
                  </div>
                </div>

                {/* ==========================================
                    HOLIDAY CONTENT
                ========================================== */}

                {day.isHoliday ? (
                  <div
                    className="
                      flex
                      min-h-[190px]
                      items-center
                      justify-center
                      bg-red-50/40
                      px-6
                      py-10
                      text-center
                    "
                  >
                    <div>
                      <div
                        className="
                          mx-auto
                          mb-4
                          flex
                          h-16
                          w-16
                          items-center
                          justify-center
                          rounded-2xl
                          bg-red-100
                          text-2xl
                          shadow-sm
                        "
                      >
                        🏖️
                      </div>

                      <p className="font-extrabold text-red-600">
                        Holiday / Day Off
                      </p>

                      <p className="mt-1 text-sm text-slate-500">
                        No appointments will be available on this day.
                      </p>

                      <div className="mx-auto mt-4 inline-flex items-center gap-2 rounded-full border border-red-200 bg-white px-3 py-1.5 text-[11px] font-bold text-red-500">
                        <span className="h-1.5 w-1.5 rounded-full bg-red-500" />
                        All sessions closed
                      </div>
                    </div>
                  </div>
                ) : (
                  /* =========================================
                     WORKING SLOTS
                  ========================================= */

                  <div className="p-4 sm:p-6">
                    {/* SLOT INFO */}

                    <div
                      className="
                        mb-5
                        flex
                        items-center
                        gap-3
                        rounded-2xl
                        border
                        border-sky-100
                        bg-sky-50/70
                        px-4
                        py-3
                      "
                    >
                      <div
                        className="
                          flex
                          h-10
                          w-10
                          shrink-0
                          items-center
                          justify-center
                          rounded-xl
                          text-white
                          shadow-sm
                        "
                        style={{
                          backgroundImage:
                            "linear-gradient(135deg, rgb(26, 58, 92), rgb(14, 165, 233))",
                        }}
                      >
                        🕐
                      </div>

                      <div>
                        <h4 className="text-sm font-extrabold text-slate-800">
                          Availability Slots
                        </h4>

                        <p className="text-xs text-slate-500">
                          Configure your working hours for this day.
                        </p>
                      </div>
                    </div>

                    {/* SLOTS */}

                    <div className="grid grid-cols-1 gap-5 xl:grid-cols-2">
                      {day.halves.map((half, halfIndex) => (
                        <div
                          key={`${day.date}-${half.name}-${halfIndex}`}
                          className={`
                              rounded-2xl
                              border
                              p-4
                              transition-all
                              duration-200
                              ${
                                half.isOpen
                                  ? "border-sky-200 bg-sky-50/50 shadow-[0_6px_22px_rgba(14,165,233,0.08)]"
                                  : "border-slate-200 bg-slate-50"
                              }
                            `}
                        >
                          {/* SLOT HEADER */}

                          <div className="mb-4 flex items-center justify-between gap-3">
                            <div className="flex items-center gap-3">
                              <div
                                className={`
                                    flex
                                    h-11
                                    w-11
                                    items-center
                                    justify-center
                                    rounded-xl
                                    text-base
                                    shadow-sm
                                    ${
                                      half.isOpen
                                        ? "text-white"
                                        : "bg-slate-200 text-slate-500"
                                    }
                                  `}
                                style={
                                  half.isOpen
                                    ? {
                                        backgroundImage:
                                          "linear-gradient(135deg, rgb(26, 58, 92), rgb(14, 165, 233))",
                                      }
                                    : undefined
                                }
                              >
                                🕐
                              </div>

                              <div>
                                <p className="font-extrabold text-slate-800">
                                  {half.name}
                                </p>

                                <p
                                  className={`
                                      mt-0.5
                                      text-xs
                                      font-bold
                                      ${
                                        half.isOpen
                                          ? "text-sky-600"
                                          : "text-slate-500"
                                      }
                                    `}
                                >
                                  <span className="mr-1">●</span>

                                  {half.isOpen ? "Available" : "Closed"}
                                </p>
                              </div>
                            </div>

                            {/* TOGGLE */}

                            <button
                              type="button"
                              onClick={() =>
                                toggleHalf(dayIndex, halfIndex, !half.isOpen)
                              }
                              className={`
                                  relative
                                  h-7
                                  w-14
                                  shrink-0
                                  rounded-full
                                  transition-all
                                  duration-200
                                  ${
                                    half.isOpen
                                      ? "shadow-[0_0_12px_rgba(14,165,233,0.35)]"
                                      : "bg-slate-300"
                                  }
                                `}
                              style={
                                half.isOpen
                                  ? {
                                      background: "rgb(14, 165, 233)",
                                    }
                                  : undefined
                              }
                              aria-label={`Toggle ${half.name}`}
                            >
                              <span
                                className={`
                                    absolute
                                    top-1
                                    h-5
                                    w-5
                                    rounded-full
                                    bg-white
                                    shadow-md
                                    transition-all
                                    duration-200
                                    ${half.isOpen ? "left-8" : "left-1"}
                                  `}
                              />
                            </button>
                          </div>

                          {/* TIME PICKER */}

                          <div
                            className={`
                                rounded-xl
                                border
                                p-3
                                ${
                                  half.isOpen
                                    ? "border-sky-100 bg-white shadow-sm"
                                    : "border-slate-200 bg-slate-100/80"
                                }
                              `}
                          >
                            <DateTimePicker
                              half={half}
                              onToggle={() =>
                                toggleHalf(dayIndex, halfIndex, !half.isOpen)
                              }
                              onTimeChange={(field, value) =>
                                updateHalfTime(
                                  dayIndex,
                                  halfIndex,
                                  field,
                                  value,
                                )
                              }
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* ==================================================
              SAVE BUTTON
          ================================================== */}

          <div className="sticky bottom-4 z-20 mt-8">
            <button
              type="button"
              onClick={handleSave}
              disabled={loading}
              className={`
                flex
                w-full
                items-center
                justify-center
                gap-3
                rounded-2xl
                py-4
                text-base
                font-extrabold
                text-white
                shadow-[0_12px_35px_rgba(14,165,233,0.25)]
                transition-all
                duration-200
                ${
                  loading
                    ? "cursor-not-allowed bg-slate-400"
                    : "hover:-translate-y-0.5 hover:shadow-[0_16px_40px_rgba(14,165,233,0.32)]"
                }
              `}
              style={
                !loading
                  ? {
                      backgroundImage:
                        "linear-gradient(135deg, rgb(26, 58, 92), rgb(14, 165, 233))",
                    }
                  : undefined
              }
            >
              {loading ? (
                <>
                  <span
                    className="
                      h-5
                      w-5
                      animate-spin
                      rounded-full
                      border-2
                      border-white/30
                      border-t-white
                    "
                  />
                  Checking & Saving...
                </>
              ) : (
                <>
                  <span className="text-lg">💾</span>
                  Save Availability
                  <span className="text-lg">→</span>
                </>
              )}
            </button>
          </div>

          {/* ==================================================
              BOTTOM BACK BUTTON
          ================================================== */}

          <div className="mt-5 flex justify-center">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="
                inline-flex
                items-center
                gap-2
                rounded-xl
                border
                border-slate-200
                bg-white
                px-5
                py-2.5
                text-sm
                font-bold
                text-slate-600
                shadow-sm
                transition-all
                hover:border-sky-200
                hover:text-sky-600
                hover:shadow-md
              "
            >
              ← Back
            </button>
          </div>

          {/* ==================================================
              FOOTER
          ================================================== */}

          <div className="py-8 text-center text-xs text-slate-400">
            <div className="mb-2 flex items-center justify-center gap-2">
              <span className="font-bold text-sky-500">✓</span>
              Secure healthcare scheduling
            </div>

            <p>© {new Date().getFullYear()} MRHealth · mrhealth.co.in</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OtherMedicalAvailability;
